import { useEffect } from 'react';

// Переводы локальные, не через i18n — как в ApparatNotWork/NetworkStatus/
// PrinterErrorBanner (см. CLAUDE.md): этот экран рендерится, когда где-то
// ВЫШЕ по дереву (не исключая сам i18n-провайдер) что-то упало, поэтому
// использовать здесь useTranslation() рискованно — если причина краша была
// в самом провайдере, хук внутри фолбэка либо бросит снова (бесконечный
// цикл падений вместо экрана ошибки), либо просто не найдёт нужный неймспейс.
// Читаем сохранённый язык напрямую из localStorage (тот же ключ, что
// i18next-browser-languagedetector использует для кэша — см. locales/i18n.js).
const translations = {
  ru: {
    title: 'Произошла ошибка',
    hint: 'Попробуйте перезагрузить страницу или вернитесь на главную.',
    home: 'На главную',
    reload: 'Перезагрузить',
  },
  en: {
    title: 'Something went wrong',
    hint: 'Try reloading the page or go back to the home screen.',
    home: 'Home',
    reload: 'Reload',
  },
  kk: {
    title: 'Қате орын алды',
    hint: 'Бетті қайта жүктеп көріңіз немесе басты бетке оралыңыз.',
    home: 'Басты бетке',
    reload: 'Қайта жүктеу',
  },
};

function getLocalLang() {
  const stored = (localStorage.getItem('i18nextLng') || '').split('-')[0];
  return translations[stored] ? stored : 'kk';
}

// Экран падения приложения. Рендерится ВНЕ обычного каркаса (ErrorBoundary
// оборачивает всё, включая Header/Footer), поэтому занимает весь экран сам.
function ErrorFallback({ error, resetErrorBoundary }) {
  const t = translations[getLocalLang()];
  useEffect(() => {
    // Electron owns recovery, including crashes before React can start.
    if (window.electronAPI?.uiWatchdog) return;
    const reloadCount = parseInt(localStorage.getItem('reloadCount') || '0', 10);
    if (reloadCount < 3) { // Ограничиваем до 3 попыток перезагрузки
      localStorage.setItem('reloadCount', (reloadCount + 1).toString());
      console.warn('Обнаружена ошибка, инициируется перезагрузка страницы:', error.message);
      const timer = setTimeout(() => window.location.reload(), 1000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // UI для отображения ошибки, если перезагрузки не помогли
  return (
    <div data-kiosk-error="true" className="min-h-screen bg-[#EDEFF4] flex items-center justify-center px-[2.4vw] py-[2vh]">
      <div className="w-full max-w-[80vw] bg-white rounded-[4vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[4vw] py-[4vh] flex flex-col items-center text-center">
        <div className="w-[12vh] h-[12vh] rounded-full bg-[#F7E1DC] flex items-center justify-center mb-[2.6vh]">
          <svg
            className="w-[5.8vh] h-[5.8vh]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#C2543E"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v6M12 16.5v.01" />
          </svg>
        </div>

        <h1 className="text-[3.2vh] font-extrabold tracking-tight">
          {t.title}
        </h1>
        <p className="text-[1.8vh] font-semibold text-[#7B8290] mt-[1.4vh] max-w-[60vw] break-words">
          {error.message}
        </p>
        <p className="text-[1.6vh] font-semibold text-[#7B8290] mt-[1vh]">
          {t.hint}
        </p>

        <div className="w-full flex gap-[1.2vw] mt-[3vh]">
          <button data-analytics="errorfallback.action.1"
            type="button"
            className="flex-1 py-[2vh] rounded-[3vh] bg-[#F1F3F7] text-[2vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
            onClick={() => (window.location.href = '/')}
          >
            {t.home}
          </button>
          <button data-analytics="errorfallback.action.2"
            type="button"
            className="flex-1 py-[2vh] rounded-[3vh] bg-[#246CE2] text-[2vh] font-bold text-white active:bg-[#1B55B4] transition-colors"
            onClick={() => {
              localStorage.setItem('reloadCount', '0'); // Сбрасываем счётчик
              resetErrorBoundary(); // Пытаемся сбросить состояние
              window.location.reload(); // Перезагружаем страницу
            }}
          >
            {t.reload}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorFallback;
